import React, { useRef, useCallback, useEffect } from 'react';
import { useAlert } from '../../../../contexts/AlertContext';
import { useImportStore } from '../../../../store/importStore';

export const useImportManager = (config) => {
  const { showAlert } = useAlert();
  const fileInputRef = useRef(null);
  
  // Zustand store selectors - individual selectors for stable references
  const loading = useImportStore(state => state.loading);
  const validating = useImportStore(state => state.validating);
  const data = useImportStore(state => state.data);
  const isDragging = useImportStore(state => state.isDragging);
  const selectedCell = useImportStore(state => state.selectedCell);
  const detailPanelExpandedRowIds = useImportStore(state => state.detailPanelExpandedRowIds);
  const editingCell = useImportStore(state => state.editingCell);
  const isSelectOpen = useImportStore(state => state.isSelectOpen);
  const deleteModal = useImportStore(state => state.deleteModal);
  const importModal = useImportStore(state => state.importModal);
  
  // Get custom state from config hooks
  const customState = useImportStore(state => state.customState || {});
  
  // Zustand store actions - individual selectors for stable references
  const setLoading = useImportStore(state => state.setLoading);
  const setValidating = useImportStore(state => state.setValidating);
  const setData = useImportStore(state => state.setData);
  const updateItem = useImportStore(state => state.updateItem);
  const deleteItemFromStore = useImportStore(state => state.deleteItem);
  const setDragging = useImportStore(state => state.setDragging);
  const setSelectedCell = useImportStore(state => state.setSelectedCell);
  const setEditingCell = useImportStore(state => state.setEditingCell);
  const setSelectOpen = useImportStore(state => state.setSelectOpen);
  const toggleDetailPanel = useImportStore(state => state.toggleDetailPanel);
  const setDeleteModal = useImportStore(state => state.setDeleteModal);
  const setImportModal = useImportStore(state => state.setImportModal);
  const setCustomState = useImportStore(state => state.setCustomState || (() => {}));

  // Zustand store actions - individual selectors for stable references
  const reset = useImportStore(state => state.reset);

  // Reset store when component unmounts or config changes
  useEffect(() => {
    // Reset store when config changes (switching between import types)
    reset();
    
    // Initialize custom state from config hooks
    if (config.hooks?.initializeCustomState) {
      config.hooks.initializeCustomState({ setCustomState });
    }
    
    return () => {
      // Reset store on unmount to prevent state leakage
      reset();
    };
  }, [config, reset, setCustomState]);

  // Generic validation
  const validateData = useCallback(async (parsedRows) => {
    setValidating(true);
    
    try {
      // Format data for API
      const apiRows = parsedRows.map((data, index) => ({
        rowIndex: index,
        data: Object.keys(config.fields).reduce((acc, fieldName) => {
          const fieldConfig = config.fields[fieldName];
          // Use prepareForValidation if available, otherwise use raw value
          acc[fieldName] = fieldConfig?.prepareForValidation 
            ? fieldConfig.prepareForValidation(data[fieldName])
            : data[fieldName];
          return acc;
        }, {})
      }));

      const response = await config.api.validate(apiRows);
      
      // Process validated data using config helper
      const validatedData = config.helpers.processValidationResponse(response, { showAlert });

      setData(validatedData);
      
      // Handle entity-specific post-validation logic
      const counts = config.helpers.getStatusCounts(validatedData);
      
      // Call post-validation hook if provided
      if (config.hooks?.onPostValidation) {
        config.hooks.onPostValidation(validatedData, {
          setCustomState,
          customState,
          showAlert
        });
      }
      
      // Show appropriate message
      if (counts.invalid > 0) {
        showAlert(`Validated ${validatedData.length} ${config.entityNamePlural.toLowerCase()}: ${counts.valid} valid, ${counts.invalid} need correction`, 'warning');
      } else {
        showAlert(`Successfully validated ${validatedData.length} ${config.entityNamePlural.toLowerCase()}`, 'success');
      }
    } catch (error) {
      showAlert(`Failed to validate ${config.entityName.toLowerCase()} data`, 'error');
      console.error(error);
    } finally {
      setValidating(false);
    }
  }, [config, showAlert, setValidating, setData, setCustomState, customState]);

  // Helper function to parse CSV row with proper handling of quoted values
  const parseCSVRow = (text) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      
      if (char === '"') {
        // Check if it's an escaped quote (doubled quotes)
        if (inQuotes && text[i + 1] === '"') {
          current += '"';
          i++; // Skip the next quote
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        // End of field
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    // Don't forget the last field
    result.push(current.trim());
    
    return result;
  };

  // Generic CSV processing
  const processFile = useCallback(async (file) => {
    if (!file.name.endsWith('.csv')) {
      showAlert('Please upload a CSV file', 'error');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target.result;
        const rows = text.split('\n').filter(row => row.trim());
        
        const headers = parseCSVRow(rows[0]).map(h => {
          // Remove surrounding quotes from headers if present
          if (h.startsWith('"') && h.endsWith('"')) {
            return h.slice(1, -1);
          }
          return h;
        });
        
        // Validate headers
        const missingHeaders = config.requiredFields.filter(h => !headers.includes(h));
        
        if (missingHeaders.length > 0) {
          showAlert(`Missing required columns: ${missingHeaders.join(', ')}`, 'error');
          return;
        }

        // Parse data with proper CSV handling for quoted values
        const parsedRows = rows.slice(1).map((row) => {
          const values = parseCSVRow(row);
          const item = {};
          headers.forEach((header, i) => {
            // Remove surrounding quotes if present
            let value = values[i] || '';
            if (value.startsWith('"') && value.endsWith('"')) {
              value = value.slice(1, -1);
            }
            item[header] = value;
          });
          
          // Ensure optional fields exist
          config.optionalFields?.forEach(field => {
            if (!Object.prototype.hasOwnProperty.call(item, field)) {
              item[field] = '';
            }
          });
          
          return item;
        }).filter(item => {
          // Filter out empty rows - check if any required field has data
          return config.requiredFields.some(field => item[field]);
        });

        if (parsedRows.length === 0) {
          showAlert(`No valid data found in CSV file`, 'error');
          return;
        }

        // Validate with API
        await validateData(parsedRows);
        
      } catch (error) {
        showAlert('Failed to parse CSV file', 'error');
        console.error(error);
      }
    };

    reader.readAsText(file);
  }, [config, showAlert, validateData, parseCSVRow]);

  // Generic import
  const importData = useCallback(async () => {
    const itemsToImport = config.helpers.getImportableItems(data);
    
    setImportModal({ open: false, count: 0 });
    setLoading(true);
    
    try {
      // Format data for API
      const apiRows = itemsToImport.map((item) => ({
        rowIndex: item.rowIndex,
        data: Object.keys(config.fields).reduce((acc, field) => {
          const fieldConfig = config.fields[field];
          // Use prepareForValidation if available, otherwise use raw value
          acc[field] = fieldConfig?.prepareForValidation 
            ? fieldConfig.prepareForValidation(item[field])
            : item[field];
          return acc;
        }, {})
      }));

      const response = await config.api.import(apiRows);
      
      // Handle response
      if (response.successfulImports > 0) {
        showAlert(response.message, response.failedImports > 0 ? 'warning' : 'success');
      }
      
      // Process import results using config helper
      const updatedData = config.helpers.processImportResponse(response, data, itemsToImport);
      
      setData(updatedData);
      
      if (response.failedImports > 0) {
        showAlert(`Import completed with errors. ${response.failedImports} ${config.entityNamePlural.toLowerCase()} failed to import. Check the grid for details.`, 'error');
      }
      
    } catch (error) {
      showAlert(error.message || `Failed to import ${config.entityNamePlural.toLowerCase()}`, 'error');
      console.error('Import error:', error);
    } finally {
      setLoading(false);
    }
  }, [config, data, showAlert, setImportModal, setLoading, setData]);

  // Generic field update
  const updateField = useCallback((itemId, field, value) => {
    const item = data.find(i => i.id === itemId);
    
    // Check if field should be marked as pending based on config
    const skipPendingFields = config.skipPendingFields || [];
    const shouldMarkPending = !skipPendingFields.includes(field);
    
    updateItem(itemId, {
      [field]: value,
      ...(shouldMarkPending && { validationStatus: 'pending' })
    });

    // Call field change hook if provided
    if (config.hooks?.onFieldChange) {
      config.hooks.onFieldChange(itemId, field, value, {
        item,
        updateItem,
        customState,
        setCustomState,
        data
      });
    }

    // Handle dependency clearing - if a field that other fields depend on changes, clear dependent fields
    Object.entries(config.fields).forEach(([dependentField, dependentFieldConfig]) => {
      if (dependentFieldConfig.dependsOn === field) {
        updateItem(itemId, { [dependentField]: '' });
      }
    });
  }, [config, updateItem, customState, setCustomState, data]);

  // Generic delete
  const deleteItem = useCallback((itemId) => {
    deleteItemFromStore(itemId);
    setDeleteModal({ open: false, itemId: null });
    showAlert(`${config.entityName} removed from import list`, 'success');
  }, [config.entityName, showAlert, deleteItemFromStore, setDeleteModal]);

  // Generic revalidation
  const revalidate = useCallback(async () => {
    if (data.length === 0) {
      showAlert(`No ${config.entityNamePlural.toLowerCase()} to validate`, 'warning');
      return;
    }

    // Format data for API
    const apiRows = data.map((item, index) => ({
      rowIndex: item.rowIndex || index,
      data: Object.keys(config.fields).reduce((acc, fieldName) => {
        const fieldConfig = config.fields[fieldName];
        // Use prepareForValidation if available, otherwise use raw value
        acc[fieldName] = fieldConfig?.prepareForValidation 
          ? fieldConfig.prepareForValidation(item[fieldName])
          : item[fieldName];
        return acc;
      }, {})
    }));

    setValidating(true);
    
    try {
      const response = await config.api.validate(apiRows);
      
      // Process revalidation results using config helper
      const revalidatedData = config.helpers.processValidationResponse(response, { showAlert });

      setData(revalidatedData);
      
      // Handle entity-specific post-revalidation logic
      const counts = config.helpers.getStatusCounts(revalidatedData);
      
      if (counts.invalid > 0) {
        showAlert(`Revalidation complete: ${counts.valid} valid, ${counts.invalid} need correction`, 'warning');
      } else {
        showAlert(`All ${revalidatedData.length} ${config.entityNamePlural.toLowerCase()} are valid`, 'success');
      }
    } catch (error) {
      showAlert(`Failed to revalidate ${config.entityName.toLowerCase()} data`, 'error');
      console.error(error);
    } finally {
      setValidating(false);
    }
  }, [config, data, showAlert, setValidating, setData]);

  // Context object for field configurations - useMemo to ensure it updates when state changes
  const fieldContext = React.useMemo(() => ({
    getFieldValue: () => null, // This will be overridden by item-specific contexts
    updateField,
    customState,
    setCustomState,
    // Allow configs to extend the field context
    ...(config.hooks?.getFieldContext?.(customState) || {})
  }), [updateField, customState, setCustomState, config]);

  return {
    state: {
      loading,
      validating,
      data,
      isDragging,
      selectedCell,
      detailPanelExpandedRowIds,
      editingCell,
      isSelectOpen,
      deleteModal,
      importModal,
      ...customState
    },
    fileInputRef,
    actions: {
      processFile,
      validateData,
      importData,
      updateField,
      deleteItem,
      revalidate,
      setDragging,
      setSelectedCell,
      setEditingCell,
      setSelectOpen,
      setDeleteModal,
      setImportModal,
      toggleDetailPanel
    },
    fieldContext
  };
}; 